// useDrivingAwareGeofence.ts — FEATURE 2: driving-aware geofence auto-stop v1 (foreground-only).
//
// WHAT THIS DOES (and, just as important, what it does NOT do):
//   • It decides WHEN to press the recorder's existing "Stop & save" — nothing more. The capture
//     pipeline (useRecorder / CaptureSheet upload flow) is completely untouched. This hook only
//     calls the caller-supplied `onAutoStop()` when it is confident the user has finished a meeting
//     in a fixed place and then walked away on foot.
//   • It NEVER records, uploads, or reads audio. It consumes two location/motion signals and runs a
//     conservative state machine on top of them.
//
// STATE MACHINE (spec-locked):
//   DISARMED → ANCHORED → STILL_ACCUMULATING → STILL_CONFIRMED (anchor lat/lng LOCKED here)
//            → WALK_AWAY_CANDIDATE → TRIP_CONFIRMED → WARN_COUNTDOWN (visible 15-20s) → onAutoStop()
//
//   DRIVE VETO: any driving evidence AFTER STILL_CONFIRMED → DRIVE_SUPPRESSED (recording left
//   untouched) and the machine resets to ANCHORED. Driving is detected by THREE independent signals,
//   ANY of which vetoes (fail-safe: a false veto merely keeps a legit recording running, whereas a
//   missed veto could stop a recording the user wanted while driving):
//     1. Activity Recognition IN_VEHICLE with confidence ≥ 50 (native plugin).
//     2. GPS speed > 15 km/h (EMA-smoothed).
//     3. Kinematics — ≥ 100 m displacement inside a ≤ 65 s window.
//
// FAIL-SAFE (hard requirement): if we are not native, permission is denied, or GPS never delivers a
// fix, the machine NEVER arms and NEVER auto-stops. `mode` is always exposed so the UI can honestly
// show Armed / Driving — auto-stop paused / Unavailable.
//
// FOREGROUND-ONLY (v1): watchPosition + activity updates run only while the recorder is mounted and
// `active` is true. No background location, no new foreground service. The @capacitor/geolocation
// import is dynamic + gated behind isNative() so the website / desktop bundle is unchanged (web →
// Unavailable, a pure no-op).

import { useCallback, useEffect, useRef, useState } from 'react';
import { isNative } from '@/lib/native/platform';

// ---------------------------------------------------------------------------
// Tunables (spec thresholds)
// ---------------------------------------------------------------------------

/** STILL dwell required before we lock an anchor: 5 minutes. */
const STILL_DWELL_MS = 5 * 60 * 1000;
/** …established via at least this many accepted samples… */
const STILL_MIN_SAMPLES = 2;
/** …spanning at least this long (anti-spoof; implied by the 5-min dwell but checked explicitly). */
const STILL_MIN_SPAN_MS = 60 * 1000;

/** Anchor radius. One radius governs both "still near the provisional anchor" and "walked away from
 *  the locked anchor". 100 m absorbs urban GPS drift while still catching a genuine departure. */
const ANCHOR_DISTANCE_M = 100;
/** Discard any fix whose reported accuracy is worse than this (metres). */
const ACCURACY_MAX_M = 50;
/** Consecutive good fixes required to accept a walk-away crossing (rejects single-fix GPS spikes). */
const CONSECUTIVE_REQUIRED = 2;

/** A gap between usable fixes longer than this invalidates a locked anchor: require a fresh STILL. */
const SIGNAL_LOSS_GAP_MS = 3 * 60 * 1000;

/** WARN banner visible window before the auto-stop actually fires (spec: 15-20 s). */
const WARN_COUNTDOWN_MS = 18 * 1000;

/** Driving-veto signal thresholds. */
const DRIVE_SPEED_MPS = 15 / 3.6; // 15 km/h → m/s (~4.17)
const IN_VEHICLE_CONF = 50;
const KINEMATIC_DISTANCE_M = 100;
const KINEMATIC_WINDOW_MS = 65 * 1000;

/** Ignore an Activity-Recognition sample older than this. */
const ACTIVITY_STALE_MS = 90 * 1000;
/** After a drive veto, wait this long with NO driving evidence before re-arming (ANCHORED). */
const DRIVE_COOLDOWN_MS = 30 * 1000;

/** Trip confirmation: a walk-away becomes a real trip once the user has stayed beyond the anchor for
 *  this long, or reached this far from it — whichever comes first. */
const TRIP_CONFIRM_MS = 45 * 1000;
const TRIP_CONFIRM_DISTANCE_M = 200;

/** EMA weight for speed smoothing. */
const SPEED_EMA_ALPHA = 0.5;

/** If armed but no fix has EVER arrived within this window, surface Unavailable (honest "no GPS"). */
const NO_FIX_UNAVAILABLE_MS = 45 * 1000;

/** Machine tick cadence (dwell timing, countdown, signal-loss). */
const TICK_MS = 1000;

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/** UI-facing mode. The internal machine has more states; these collapse to what the banner shows. */
export type GeofenceMode =
  | 'unavailable' // not native / permission denied / no GPS — never arms, never stops
  | 'disarmed' // not recording
  | 'armed' // watching; will auto-stop if the user settles then leaves on foot
  | 'driving-suppressed' // driving detected after a still — auto-stop paused
  | 'warning'; // countdown banner is live

/** Internal state-machine label (also surfaced for debugging / status copy). */
export type GeofenceState =
  | 'UNAVAILABLE'
  | 'DISARMED'
  | 'ANCHORED'
  | 'STILL_ACCUMULATING'
  | 'STILL_CONFIRMED'
  | 'WALK_AWAY_CANDIDATE'
  | 'TRIP_CONFIRMED'
  | 'WARN_COUNTDOWN'
  | 'DRIVE_SUPPRESSED';

export interface DrivingAwareGeofenceOptions {
  /** Arm while true (the recorder is live); disarm on false. */
  active: boolean;
  /** Called exactly once when the machine decides to press Stop. */
  onAutoStop: () => void;
}

export interface UseDrivingAwareGeofence {
  mode: GeofenceMode;
  state: GeofenceState;
  /** Whole seconds left in the WARN countdown while mode === 'warning', else null. */
  warnSecondsRemaining: number | null;
  /** User tapped "Keep recording" on the banner — abort this auto-stop, fall back to watching. */
  cancelWarning: () => void;
  /** User tapped "Stop now" — fire the auto-stop immediately. */
  stopNow: () => void;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function distMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ---------------------------------------------------------------------------
// Machine model (kept in a ref — pure data, mutated by the fix/activity/tick handlers)
// ---------------------------------------------------------------------------

interface Fix {
  t: number;
  lat: number;
  lng: number;
  acc: number;
}

interface Machine {
  state: GeofenceState;
  armedAt: number;
  hasEverFixed: boolean;

  // Provisional anchor (pre-lock): the candidate still-point we are accumulating dwell against.
  provAnchor: { lat: number; lng: number } | null;
  provFirstAt: number | null;
  provSamples: number;
  /** True once IN_VEHICLE (conf ≥ 50) has been seen since the provisional anchor was seated. */
  inVehicleSinceAnchor: boolean;

  // Locked anchor (set AT STILL_CONFIRMED) — the meeting location.
  lockedAnchor: { lat: number; lng: number } | null;

  // Walk-away / trip tracking against the locked anchor.
  awayConsecutive: number;
  tripFirstAwayAt: number | null;
  maxAwayDistance: number;

  // Warn.
  warnEndsAt: number | null;

  // Drive veto cooldown.
  drivingClearSince: number | null;

  // Signals.
  lastFixAt: number | null;
  smoothedSpeed: number | null;
  fixBuffer: Fix[];
  lastActivity: { type: string; confidence: number; at: number } | null;

  // Guard: onAutoStop must fire at most once per armed session.
  fired: boolean;
}

function freshMachine(now: number): Machine {
  return {
    state: 'ANCHORED',
    armedAt: now,
    hasEverFixed: false,
    provAnchor: null,
    provFirstAt: null,
    provSamples: 0,
    inVehicleSinceAnchor: false,
    lockedAnchor: null,
    awayConsecutive: 0,
    tripFirstAwayAt: null,
    maxAwayDistance: 0,
    warnEndsAt: null,
    drivingClearSince: null,
    lastFixAt: null,
    smoothedSpeed: null,
    fixBuffer: [],
    lastActivity: null,
    fired: false,
  };
}

function toMode(state: GeofenceState): GeofenceMode {
  switch (state) {
    case 'UNAVAILABLE':
      return 'unavailable';
    case 'DISARMED':
      return 'disarmed';
    case 'DRIVE_SUPPRESSED':
      return 'driving-suppressed';
    case 'WARN_COUNTDOWN':
      return 'warning';
    default:
      return 'armed';
  }
}

// ---------------------------------------------------------------------------
// Signal helpers
// ---------------------------------------------------------------------------

function kinematicMovement(m: Machine): boolean {
  const buf = m.fixBuffer;
  if (buf.length < 2) return false;
  const latest = buf[buf.length - 1];
  for (let i = 0; i < buf.length - 1; i++) {
    const f = buf[i];
    const dt = latest.t - f.t;
    if (dt > 0 && dt <= KINEMATIC_WINDOW_MS) {
      if (distMeters(f.lat, f.lng, latest.lat, latest.lng) >= KINEMATIC_DISTANCE_M) return true;
    }
  }
  return false;
}

function isDriving(m: Machine, now: number): boolean {
  const act = m.lastActivity;
  if (
    act &&
    act.type === 'IN_VEHICLE' &&
    act.confidence >= IN_VEHICLE_CONF &&
    now - act.at <= ACTIVITY_STALE_MS
  ) {
    return true;
  }
  if (m.smoothedSpeed != null && m.smoothedSpeed > DRIVE_SPEED_MPS) return true;
  if (kinematicMovement(m)) return true;
  return false;
}

/** Seat (or re-seat) the provisional still anchor at the given fix, resetting dwell accounting. */
function seatProvisional(m: Machine, fix: Fix): void {
  m.provAnchor = { lat: fix.lat, lng: fix.lng };
  m.provFirstAt = fix.t;
  m.provSamples = 1;
  m.inVehicleSinceAnchor = false;
  m.state = 'STILL_ACCUMULATING';
}

/** Drop everything back to ANCHORED (keeps signal buffers so driving detection stays warm). */
function resetToAnchored(m: Machine): void {
  m.state = 'ANCHORED';
  m.provAnchor = null;
  m.provFirstAt = null;
  m.provSamples = 0;
  m.inVehicleSinceAnchor = false;
  m.lockedAnchor = null;
  m.awayConsecutive = 0;
  m.tripFirstAwayAt = null;
  m.maxAwayDistance = 0;
  m.warnEndsAt = null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDrivingAwareGeofence(
  opts: DrivingAwareGeofenceOptions,
): UseDrivingAwareGeofence {
  const { active, onAutoStop } = opts;

  const [state, setStateLabel] = useState<GeofenceState>('DISARMED');
  const [warnSecondsRemaining, setWarnSeconds] = useState<number | null>(null);

  // Latest onAutoStop kept in a ref so the long-lived machine never fires a stale closure.
  const onAutoStopRef = useRef(onAutoStop);
  useEffect(() => {
    onAutoStopRef.current = onAutoStop;
  }, [onAutoStop]);

  const machineRef = useRef<Machine | null>(null);
  const watchIdRef = useRef<string | null>(null);
  const activityHandleRef = useRef<{ remove: () => Promise<void> } | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Publish the machine's current state to React (drives mode + banner). Called after every mutation.
  const publish = useCallback(() => {
    const m = machineRef.current;
    if (!m) {
      setStateLabel('DISARMED');
      setWarnSeconds(null);
      return;
    }
    setStateLabel(m.state);
    if (m.state === 'WARN_COUNTDOWN' && m.warnEndsAt != null) {
      setWarnSeconds(Math.max(0, Math.ceil((m.warnEndsAt - Date.now()) / 1000)));
    } else {
      setWarnSeconds(null);
    }
  }, []);

  // Fire the auto-stop exactly once, then disarm the machine's active behaviour (the caller will flip
  // `active` false as the recording stops, which tears everything down via the effect cleanup).
  const fireAutoStop = useCallback(() => {
    const m = machineRef.current;
    if (!m || m.fired) return;
    m.fired = true;
    m.state = 'DISARMED';
    m.warnEndsAt = null;
    publish();
    try {
      onAutoStopRef.current();
    } catch {
      /* the recorder's stop is best-effort from our side */
    }
  }, [publish]);

  // -- GPS fix handler ---------------------------------------------------------------------------
  const onFix = useCallback(
    (lat: number, lng: number, acc: number, speed: number | null, ts: number) => {
      const m = machineRef.current;
      if (!m || m.state === 'UNAVAILABLE' || m.fired) return;

      // Discard low-quality fixes entirely — they must not seat anchors or trip walk-aways.
      if (!Number.isFinite(acc) || acc > ACCURACY_MAX_M) return;

      const now = ts && Number.isFinite(ts) ? ts : Date.now();
      m.hasEverFixed = true;
      m.lastFixAt = now;

      // Smooth speed (EMA). Treat null/negative as "no reading" and hold the previous smoothed value.
      if (speed != null && Number.isFinite(speed) && speed >= 0) {
        m.smoothedSpeed =
          m.smoothedSpeed == null
            ? speed
            : SPEED_EMA_ALPHA * speed + (1 - SPEED_EMA_ALPHA) * m.smoothedSpeed;
      }

      // Maintain the kinematics buffer (only fixes inside the window matter).
      const fix: Fix = { t: now, lat, lng, acc };
      m.fixBuffer.push(fix);
      const cutoff = now - KINEMATIC_WINDOW_MS;
      while (m.fixBuffer.length > 1 && m.fixBuffer[0].t < cutoff) m.fixBuffer.shift();

      const driving = isDriving(m, now);

      switch (m.state) {
        case 'ANCHORED': {
          if (driving) {
            m.provAnchor = null;
            break; // can't anchor a still-point while moving
          }
          seatProvisional(m, fix);
          break;
        }

        case 'STILL_ACCUMULATING': {
          if (driving || !m.provAnchor || m.provFirstAt == null) {
            // Movement (or a lost anchor) → not still; drop back and wait to re-seat.
            resetToAnchored(m);
            break;
          }
          const d = distMeters(fix.lat, fix.lng, m.provAnchor.lat, m.provAnchor.lng);
          if (d > ANCHOR_DISTANCE_M) {
            // Wandered out of the still radius — re-seat the provisional anchor here.
            seatProvisional(m, fix);
            break;
          }
          m.provSamples += 1;
          const span = now - m.provFirstAt;
          if (
            span >= STILL_DWELL_MS &&
            span >= STILL_MIN_SPAN_MS &&
            m.provSamples >= STILL_MIN_SAMPLES &&
            !m.inVehicleSinceAnchor
          ) {
            // CONFIRM: lock the anchor at the provisional still-point.
            m.lockedAnchor = { lat: m.provAnchor.lat, lng: m.provAnchor.lng };
            m.awayConsecutive = 0;
            m.tripFirstAwayAt = null;
            m.maxAwayDistance = 0;
            m.state = 'STILL_CONFIRMED';
          }
          break;
        }

        case 'STILL_CONFIRMED': {
          if (driving) {
            m.state = 'DRIVE_SUPPRESSED';
            m.drivingClearSince = null;
            break;
          }
          if (!m.lockedAnchor) {
            resetToAnchored(m);
            break;
          }
          const d = distMeters(fix.lat, fix.lng, m.lockedAnchor.lat, m.lockedAnchor.lng);
          if (d > ANCHOR_DISTANCE_M) {
            m.awayConsecutive += 1;
            if (m.awayConsecutive >= CONSECUTIVE_REQUIRED) {
              m.state = 'WALK_AWAY_CANDIDATE';
              m.tripFirstAwayAt = now;
              m.maxAwayDistance = d;
            }
          } else {
            m.awayConsecutive = 0;
          }
          break;
        }

        case 'WALK_AWAY_CANDIDATE': {
          if (driving) {
            m.state = 'DRIVE_SUPPRESSED';
            m.drivingClearSince = null;
            break;
          }
          if (!m.lockedAnchor || m.tripFirstAwayAt == null) {
            resetToAnchored(m);
            break;
          }
          const d = distMeters(fix.lat, fix.lng, m.lockedAnchor.lat, m.lockedAnchor.lng);
          if (d <= ANCHOR_DISTANCE_M) {
            // Came back inside the radius — false departure, resume watching from the locked anchor.
            m.awayConsecutive = 0;
            m.tripFirstAwayAt = null;
            m.state = 'STILL_CONFIRMED';
            break;
          }
          m.maxAwayDistance = Math.max(m.maxAwayDistance, d);
          const sustained = now - m.tripFirstAwayAt >= TRIP_CONFIRM_MS;
          if (sustained || d >= TRIP_CONFIRM_DISTANCE_M) {
            // The departure is now a confirmed trip (TRIP_CONFIRMED) — open the visible warn countdown
            // immediately rather than dwelling in a no-op intermediate state.
            m.state = 'WARN_COUNTDOWN';
            m.warnEndsAt = now + WARN_COUNTDOWN_MS;
          }
          break;
        }

        case 'TRIP_CONFIRMED': {
          // Transient — normally we flip straight to WARN_COUNTDOWN above. If we ever land here on a
          // fix, honour the drive veto then arm the countdown.
          if (driving) {
            m.state = 'DRIVE_SUPPRESSED';
            m.drivingClearSince = null;
            break;
          }
          m.state = 'WARN_COUNTDOWN';
          m.warnEndsAt = now + WARN_COUNTDOWN_MS;
          break;
        }

        case 'WARN_COUNTDOWN': {
          if (driving) {
            // Driving mid-countdown → veto, abort the pending stop.
            m.state = 'DRIVE_SUPPRESSED';
            m.drivingClearSince = null;
            m.warnEndsAt = null;
            break;
          }
          if (m.lockedAnchor) {
            const d = distMeters(fix.lat, fix.lng, m.lockedAnchor.lat, m.lockedAnchor.lng);
            if (d <= ANCHOR_DISTANCE_M) {
              // Returned to the anchor before the countdown elapsed — cancel and resume watching.
              m.warnEndsAt = null;
              m.awayConsecutive = 0;
              m.tripFirstAwayAt = null;
              m.state = 'STILL_CONFIRMED';
            }
          }
          break;
        }

        case 'DRIVE_SUPPRESSED': {
          // Stay suppressed while any driving evidence persists; re-arm once it's been clear a while.
          if (driving) {
            m.drivingClearSince = null;
          } else {
            if (m.drivingClearSince == null) m.drivingClearSince = now;
            if (now - m.drivingClearSince >= DRIVE_COOLDOWN_MS) {
              resetToAnchored(m);
            }
          }
          break;
        }

        default:
          break;
      }

      publish();
    },
    [publish],
  );

  // -- Activity-Recognition event handler --------------------------------------------------------
  const onActivity = useCallback(
    (type: string, confidence: number) => {
      const m = machineRef.current;
      if (!m || m.state === 'UNAVAILABLE' || m.fired) return;
      const now = Date.now();
      m.lastActivity = { type, confidence, at: now };

      const vehicle = type === 'IN_VEHICLE' && confidence >= IN_VEHICLE_CONF;
      if (!vehicle) {
        publish();
        return;
      }

      // A confident IN_VEHICLE reading is enough on its own to act.
      if (m.state === 'STILL_ACCUMULATING' || m.state === 'ANCHORED') {
        // Pre-lock: a vehicle reading means "not still" — poison the current accumulation.
        m.inVehicleSinceAnchor = true;
        if (m.state === 'STILL_ACCUMULATING') resetToAnchored(m);
      } else if (
        m.state === 'STILL_CONFIRMED' ||
        m.state === 'WALK_AWAY_CANDIDATE' ||
        m.state === 'TRIP_CONFIRMED' ||
        m.state === 'WARN_COUNTDOWN'
      ) {
        // Post-lock: this is the drive VETO.
        m.state = 'DRIVE_SUPPRESSED';
        m.drivingClearSince = null;
        m.warnEndsAt = null;
      }
      publish();
    },
    [publish],
  );

  // -- Periodic tick: dwell/countdown timing + signal-loss invalidation --------------------------
  const onTick = useCallback(() => {
    const m = machineRef.current;
    if (!m || m.fired) return;
    const now = Date.now();

    if (m.state === 'UNAVAILABLE') {
      publish();
      return;
    }

    // Honest "no GPS" surfacing: armed but never received a fix within the grace window.
    if (!m.hasEverFixed && now - m.armedAt >= NO_FIX_UNAVAILABLE_MS) {
      m.state = 'UNAVAILABLE';
      publish();
      return;
    }

    // Signal-loss: a >3-min gap invalidates any locked anchor / in-flight departure → fresh STILL.
    if (
      m.hasEverFixed &&
      m.lastFixAt != null &&
      now - m.lastFixAt > SIGNAL_LOSS_GAP_MS &&
      m.state !== 'DRIVE_SUPPRESSED'
    ) {
      resetToAnchored(m);
      publish();
      return;
    }

    // Dwell can also complete purely on elapsed time (fixes may arrive slower than once/sec, so the
    // confirm check on the fix path could otherwise wait for the next in-radius fix). Re-check here.
    if (
      m.state === 'STILL_ACCUMULATING' &&
      m.provFirstAt != null &&
      m.provAnchor != null &&
      m.provSamples >= STILL_MIN_SAMPLES &&
      !m.inVehicleSinceAnchor
    ) {
      const span = now - m.provFirstAt;
      if (span >= STILL_DWELL_MS && span >= STILL_MIN_SPAN_MS) {
        m.lockedAnchor = { lat: m.provAnchor.lat, lng: m.provAnchor.lng };
        m.awayConsecutive = 0;
        m.tripFirstAwayAt = null;
        m.maxAwayDistance = 0;
        m.state = 'STILL_CONFIRMED';
      }
    }

    // Drive-suppression cooldown may elapse without new fixes.
    if (m.state === 'DRIVE_SUPPRESSED' && !isDriving(m, now)) {
      if (m.drivingClearSince == null) m.drivingClearSince = now;
      else if (now - m.drivingClearSince >= DRIVE_COOLDOWN_MS) resetToAnchored(m);
    }

    // Warn countdown expiry → auto-stop.
    if (m.state === 'WARN_COUNTDOWN' && m.warnEndsAt != null && now >= m.warnEndsAt) {
      fireAutoStop();
      return;
    }

    publish();
  }, [publish, fireAutoStop]);

  // -- User banner actions -----------------------------------------------------------------------
  const cancelWarning = useCallback(() => {
    const m = machineRef.current;
    if (!m || m.state !== 'WARN_COUNTDOWN') return;
    // "Keep recording" — abort this stop and resume watching from the locked anchor (or re-anchor).
    m.warnEndsAt = null;
    m.awayConsecutive = 0;
    m.tripFirstAwayAt = null;
    m.state = m.lockedAnchor ? 'STILL_CONFIRMED' : 'ANCHORED';
    publish();
  }, [publish]);

  const stopNow = useCallback(() => {
    const m = machineRef.current;
    if (!m || m.state !== 'WARN_COUNTDOWN') return;
    fireAutoStop();
  }, [fireAutoStop]);

  // -- Arm / disarm lifecycle --------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    // Disarmed / web / non-native: hard no-op. Never arms, never stops.
    if (!active) {
      machineRef.current = null;
      setStateLabel('DISARMED');
      setWarnSeconds(null);
      return;
    }
    if (!isNative()) {
      machineRef.current = null;
      setStateLabel('UNAVAILABLE');
      setWarnSeconds(null);
      return;
    }

    // Local teardown captured for cleanup.
    const stopTick = () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };

    (async () => {
      // 1. Location permission is mandatory. Denied → Unavailable, never arms.
      let granted = false;
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        let status = await Geolocation.checkPermissions();
        if (status.location !== 'granted' && status.coarseLocation !== 'granted') {
          status = await Geolocation.requestPermissions({ permissions: ['location'] });
        }
        granted = status.location === 'granted' || status.coarseLocation === 'granted';
      } catch {
        granted = false;
      }
      if (cancelled) return;
      if (!granted) {
        machineRef.current = { ...freshMachine(Date.now()), state: 'UNAVAILABLE' };
        setStateLabel('UNAVAILABLE');
        setWarnSeconds(null);
        return;
      }

      // 2. Seed the machine and start the tick.
      machineRef.current = freshMachine(Date.now());
      setStateLabel('ANCHORED');
      setWarnSeconds(null);
      stopTick();
      tickRef.current = setInterval(onTick, TICK_MS);

      // 3. GPS watch. A watch error is non-fatal: the 3-min signal-loss reset + the no-fix grace
      //    window keep us fail-safe (we simply never confirm, so we never stop a legit recording).
      try {
        const { Geolocation } = await import('@capacitor/geolocation');
        const id = await Geolocation.watchPosition(
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
          (position, err) => {
            if (cancelled || err || !position) return;
            const c = position.coords;
            onFix(c.latitude, c.longitude, c.accuracy, c.speed ?? null, position.timestamp);
          },
        );
        if (cancelled) {
          // Effect torn down while the watch was being created — clear it immediately.
          try {
            await Geolocation.clearWatch({ id });
          } catch {
            /* ignore */
          }
          return;
        }
        watchIdRef.current = id;
      } catch {
        /* watch unavailable — machine stays armed but can never confirm (fail-safe) */
      }

      // 4. Native Activity-Recognition plugin (best-effort, bonus signal). Absent plugin / denied AR
      //    permission simply means we rely on GPS speed + kinematics for the drive veto.
      try {
        const { registerPlugin } = await import('@capacitor/core');
        const plugin = registerPlugin<{
          arm(): Promise<void>;
          disarm(): Promise<void>;
          addListener(
            eventName: 'activity',
            listenerFunc: (data: { type: string; confidence: number }) => void,
          ): Promise<{ remove: () => Promise<void> }>;
        }>('DrivingAwareGeofence');
        const handle = await plugin.addListener('activity', (data) => {
          if (cancelled || !data) return;
          onActivity(String(data.type ?? 'UNKNOWN'), Number(data.confidence ?? 0));
        });
        if (cancelled) {
          try {
            await handle.remove();
          } catch {
            /* ignore */
          }
          return;
        }
        activityHandleRef.current = handle;
        await plugin.arm();
      } catch {
        /* AR plugin unavailable — degrade gracefully to the two GPS-derived signals */
      }
    })();

    return () => {
      cancelled = true;
      stopTick();
      // Tear the native subscriptions down. All best-effort; never throws into React.
      const wid = watchIdRef.current;
      watchIdRef.current = null;
      if (wid) {
        void import('@capacitor/geolocation')
          .then(({ Geolocation }) => Geolocation.clearWatch({ id: wid }))
          .catch(() => {});
      }
      const handle = activityHandleRef.current;
      activityHandleRef.current = null;
      if (handle) void handle.remove().catch(() => {});
      void import('@capacitor/core')
        .then(({ registerPlugin }) =>
          registerPlugin<{ disarm(): Promise<void> }>('DrivingAwareGeofence').disarm(),
        )
        .catch(() => {});
      machineRef.current = null;
    };
    // onFix/onActivity/onTick are stable useCallbacks; re-run only when arming toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return {
    mode: toMode(state),
    state,
    warnSecondsRemaining,
    cancelWarning,
    stopNow,
  };
}
